import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Modal from '../components/Modal';
import GlobalLoading from '../components/GlobalLoading';

export default function ManageImages() {
    const [images, setImages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [newImageName, setNewImageName] = useState('');
    const [newImageBase64, setNewImageBase64] = useState('');
    const [previewUrl, setPreviewUrl] = useState('');
    const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'alert', onConfirm: null });
    const fileInputRef = useRef(null);

    // Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const imagesPerPage = 9;

    // Lógica para determinar imagens da página atual
    const totalPages = Math.ceil(images.length / imagesPerPage);
    const currentImages = images.slice((currentPage - 1) * imagesPerPage, currentPage * imagesPerPage);

    const closeModal = () => setModal(prev => ({ ...prev, isOpen: false }));
    const showModal = (title, message, type = 'alert', onConfirm = null) => {
        setModal({ isOpen: true, title, message, type, onConfirm });
    };

    const handleClearFile = () => {
        setNewImageBase64('');
        setPreviewUrl('');
        setNewImageName('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    useEffect(() => {
        fetchImages();
    }, []);

    const fetchImages = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/imageRoutes`);
            if (Array.isArray(response.data)) {
                setImages(response.data);
            }
        } catch (error) {
            console.error('Erro ao buscar imagens:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
            showModal('Atenção', 'Formato de imagem inválido. Por favor, selecione apenas imagens JPG, JPEG ou PNG.', 'error');
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        // Pré-visualização e conversão para Base64
        const reader = new FileReader();
        reader.onloadend = () => {
            setNewImageBase64(reader.result);
            setPreviewUrl(reader.result);
            if (!newImageName) {
                // Sugere o nome do arquivo sem extensão
                setNewImageName(file.name.replace(/\.[^/.]+$/, ""));
            }
        };
        reader.readAsDataURL(file);
    };

    const handleUpload = async () => {
        if (!newImageName || !newImageBase64) {
            showModal('Atenção', 'Por favor, informe um nome e selecione uma imagem.', 'alert');
            return;
        }

        setIsUploading(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/imageRoutes`, {
                nome: newImageName,
                imagem_base64: newImageBase64
            });
            
            // Limpa o formulário
            setNewImageName('');
            setNewImageBase64('');
            setPreviewUrl('');
            if (fileInputRef.current) fileInputRef.current.value = '';
            
            // Recarrega a lista
            fetchImages();
            showModal('Sucesso', 'Imagem salva com sucesso!', 'success');
        } catch (error) {
            console.error('Erro ao fazer upload:', error);
            showModal('Erro', 'Erro ao salvar a imagem.', 'error');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDeleteClick = (id) => {
        showModal(
            'Excluir Imagem',
            'Tem certeza que deseja excluir esta imagem?',
            'confirm',
            () => executeDelete(id)
        );
    };

    const executeDelete = async (id) => {
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL || "https://api.clelton-churrasqueiras.site"}/imageRoutes/${id}`);
            setImages(images.filter(img => img.id !== id));
        } catch (error) {
            console.error('Erro ao excluir:', error);
            showModal('Erro', 'Erro ao excluir a imagem.', 'error');
        }
    };

    return (
        <div className="p-4 md:p-8 animate-fade-in max-w-7xl mx-auto">
            <div className="flex flex-col mb-8">
                <h1 className="text-3xl text-white font-bold tracking-wide">Gerenciamento de Imagens</h1>
                <p className="text-gray-400 mt-2 text-sm">Upload e controle de imagens para os orçamentos</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Coluna Esquerda: Formulário de Upload */}
                <div className="lg:col-span-1">
                    <div className="glass-panel p-6">
                        <h2 className="text-xl font-bold text-white mb-4">Nova Imagem</h2>
                        <div className="h-px bg-white/10 w-full mb-6"></div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-200 mb-1">Nome da Imagem</label>
                                <input
                                    type="text"
                                    value={newImageName}
                                    onChange={(e) => setNewImageName(e.target.value)}
                                    placeholder="Ex: Churrasqueira 5 Espetos"
                                    className="glass-input w-full"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-200 mb-2">Arquivo</label>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                    <label className="cursor-pointer bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 text-sm font-semibold py-2 px-4 rounded-full transition-colors text-center inline-block">
                                        Selecionar Imagem
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            ref={fileInputRef}
                                            className="hidden"
                                        />
                                    </label>
                                    
                                    {previewUrl ? (
                                        <button 
                                            onClick={handleClearFile}
                                            className="text-sm text-red-400 hover:text-red-300 font-semibold transition-colors bg-red-500/10 hover:bg-red-500/20 py-2 px-4 rounded-full text-center"
                                        >
                                            Limpar Imagem
                                        </button>
                                    ) : (
                                        <span className="text-sm text-gray-500">Nenhuma selecionada</span>
                                    )}
                                </div>
                            </div>

                            {previewUrl && (
                                <div className="mt-4 flex justify-center bg-black/20 rounded-xl p-2 border border-white/5">
                                    <img src={previewUrl} alt="Preview" className="max-h-48 object-contain rounded-lg" />
                                </div>
                            )}

                            <button
                                onClick={handleUpload}
                                disabled={isUploading || !newImageBase64}
                                className={`w-full py-3 mt-4 rounded-xl font-medium transition-all flex justify-center items-center ${
                                    isUploading || !newImageBase64 
                                    ? 'bg-white/5 text-gray-500 cursor-not-allowed' 
                                    : 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_15px_rgba(37,99,235,0.4)]'
                                }`}
                            >
                                {isUploading ? 'Salvando...' : 'Salvar Imagem'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Coluna Direita: Galeria */}
                <div className="lg:col-span-2">
                    <div className="glass-panel p-6 h-full">
                        <h2 className="text-xl font-bold text-white mb-4">Galeria de Imagens</h2>
                        <div className="h-px bg-white/10 w-full mb-6"></div>

                        {images.length === 0 && !isLoading ? (
                            <div className="text-center text-gray-400 py-10 bg-white/5 rounded-xl border border-white/5">
                                Nenhuma imagem cadastrada.
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {currentImages.map((img) => (
                                    <div key={img.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex flex-col group hover:bg-white/10 transition-colors">
                                        <div className="h-32 mb-3 bg-black/30 rounded-lg flex items-center justify-center overflow-hidden">
                                            <img 
                                                src={img.imagem_base64} 
                                                alt={img.nome} 
                                                className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform"
                                            />
                                        </div>
                                        <div className="flex justify-between items-start mt-auto">
                                            <p className="text-sm text-gray-200 font-medium truncate pr-2" title={img.nome}>
                                                {img.nome}
                                            </p>
                                            <button
                                                onClick={() => handleDeleteClick(img.id)}
                                                className="text-red-400 hover:text-red-300 p-1.5 rounded-lg hover:bg-red-500/20 transition-colors"
                                                title="Excluir"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {/* Table Pagination (Estoque Style) */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center mt-6 space-x-2">
                                {Array.from({ length: totalPages }, (_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentPage(index + 1)}
                                        className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all ${currentPage === index + 1
                                            ? 'bg-primary text-white shadow-lg border border-primary/50'
                                            : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-white/5 hover:text-white'
                                            }`}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal 
                isOpen={modal.isOpen} 
                title={modal.title} 
                message={modal.message} 
                type={modal.type} 
                onClose={closeModal} 
                onConfirm={modal.onConfirm} 
            />

            <GlobalLoading isActive={isLoading || isUploading} />
        </div>
    );
}
